import React, { ReactElement } from 'react';
import SwiperCore, { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Flex, Section, Typography } from 'components';

import 'swiper/swiper.scss';
import 'swiper/components/navigation/navigation.scss';
import './styles.scss';

import { DashboardButtonMore, DashboardVisualizationTypeEnum, NextSheet, SchoolDashboardArea, useSheets } from 'features/dashboard-new';
import { NavigationWrapper, NavigationButton, NavigationIcon } from './styles';
import { ProductsCardNew } from 'features/products';

SwiperCore.use([Navigation]);

interface SheetCarouselProps {
  area: SchoolDashboardArea;
  dashboardVisualizationType: DashboardVisualizationTypeEnum;
  nextSheetUuid?: string;
}

const SheetCarousel = ({ area, dashboardVisualizationType, nextSheetUuid }: SheetCarouselProps): ReactElement => {
  const { sheetTheme } = useSheets();

  const theme = sheetTheme(dashboardVisualizationType, area.dashboardAreaColor);

  const nextRef = React.useRef<HTMLDivElement>(null);
  const prevRef = React.useRef<HTMLDivElement>(null);

  return (
    <>
      <Flex
        id={`dashboard-area-${area.uuid}`}
        className="dashboard-area"
        flexDirection="column"
        flexGrow={0}
        bg={theme.sheetBg}
        py={{ m: 30, t: 80, d: 100 }}
      >
        <Section flexDirection="column" flexGrow={0}>
          <Flex mb={5} alignItems="flex-end" justifyContent="space-between">
            <Flex flexDirection="column">
              {!!area.hat && (
                <Typography type="type3" upper mb={1} color={theme.titleColor}>
                  {area.hat}
                </Typography>
              )}

              <Typography as="h2" type="type8" fontWeight="bold" color={theme.titleColor}>
                {area.name}
              </Typography>
            </Flex>

            <Flex alignItems="center" flexGrow={0}>
              <DashboardButtonMore area={area} />

              <NavigationWrapper className="carousel-nav-wrapper" ml={3}>
                <NavigationButton className="carousel-nav-left" {...theme} ref={prevRef}>
                  <NavigationIcon icon="chevronLeft" />
                </NavigationButton>

                <NavigationButton className="carousel-nav-right" {...theme} ref={nextRef}>
                  <NavigationIcon icon="chevronRight" />
                </NavigationButton>
              </NavigationWrapper>
            </Flex>
          </Flex>

          <Swiper
            loop={false}
            autoplay={{
              delay: 1000,
            }}
            breakpoints={{
              0: {
                slidesPerView: 1,
              },
              640: {
                slidesPerView: 2,
              },
              1024: {
                slidesPerView: 4,
              },
            }}
            spaceBetween={20}
            navigation={{
              prevEl: prevRef.current ? prevRef.current : undefined,
              nextEl: nextRef.current ? nextRef.current : undefined,
            }}
            onInit={(swiper) => {
              swiper.navigation.init();

              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              // eslint-disable-next-line no-param-reassign
              swiper.params.navigation.prevEl = prevRef.current;

              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              // eslint-disable-next-line no-param-reassign
              swiper.params.navigation.nextEl = nextRef.current;

              swiper.navigation.update();
            }}
            className="sheet-carousel"
          >
            {area.cards.map((card, index) => {
              return (
                <SwiperSlide key={`sheet-carousel${index.toString()}-${card.uuid}`}>
                  <ProductsCardNew card={card} dashboardAreaColor={area.dashboardAreaColor} dashboardVisualizationType={dashboardVisualizationType} />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </Section>
      </Flex>

      <NextSheet nextSheetUuid={nextSheetUuid} dashboardAreaColor={area.dashboardAreaColor} dashboardVisualizationType={dashboardVisualizationType} />
    </>
  );
};

export default SheetCarousel;
